<template>
  <div class="sm:py-12">
    <div class="sm:px-6 lg:px-8">
      <!-- Offline State -->
      <div v-if="!isOnline" class="flex flex-col justify-center items-center text-center sm:py-12">
        <div class="mb-6 text-6xl text-gray-400 dark:text-gray-600">
          <i class="pi pi-wifi"></i>
        </div>
        <h2 class="mb-2 text-2xl font-bold text-gray-700 dark:text-gray-300">You're offline</h2>
        <p class="mb-6 text-gray-600 dark:text-gray-400">Please check your internet connection to view your orders.</p>
        <button
          @click="checkConnection"
          class="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2">
          Try Again
        </button>
      </div>

      <!-- Loading State -->
      <div v-else-if="isLoading" class="flex justify-center items-center sm:py-12">
        <div class="w-12 h-12 rounded-full border-t-2 border-b-2 border-blue-500 animate-spin"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="relative px-4 py-3 mb-6 text-red-700 bg-red-100 rounded border border-red-400"
        role="alert">
        <strong class="font-bold">Error!</strong>
        <div class="block mr-2 sm:inline">{{ error }}</div>
        <button @click="fetchSingleOrder"
          class="px-4 py-2 text-white bg-green-600 rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2">
          Try Again
        </button>
      </div>

      <!-- Order Content -->
      <div v-else-if="order">
        <!-- Order Status -->
        <div class="overflow-hidden mb-8 bg-white shadow-sm dark:bg-gray-800 sm:rounded-lg">
          <div class="p-6 text-gray-900 dark:text-gray-100">
            <div>
              <div class="items-center space-y-4 sm:space-y-0 sm:justify-between sm:flex">
                <div>
                  <h3 class="text-lg font-semibold sm:text-xl">Order Status</h3>
                  <div class="flex items-center mt-2" v-if="user && user.role === USER_ROLES.CUSTOMER">
                    <span
                      :class="[
                        'ml-4 px-3 py-1 text-sm font-semibold rounded-full capitalize',
                        statusColors[order.status]
                      ]">
                      {{ orderStatusLabels[order.status] }}
                    </span>
                  </div>
                  <select v-else v-model="order.status" @change="updateOrderStatus" :disabled="isUpdatingStatus"
                    class="inline-flex px-2 py-1 mt-2 rounded border border-gray-700">
                    <option value="pending">Pending</option>
                    <option value="processing">Processing</option>
                    <option value="ready_for_pickup">Ready for Pickup</option>
                    <option value="picked_up">Picked Up</option>
                    <option value="in_transit">In Transit</option>
                    <option value="delivered">Delivered</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="refunded">Refunded</option>
                  </select>
                </div>
                <div>
                  <h3 class="text-lg font-semibold sm:text-xl">Payment Status</h3>
                  <div class="flex items-center mt-2">
                    <span
                      :class="[
                        'px-3 py-1 text-sm font-semibold rounded-full',
                        order.payment_status === 'paid'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      ]">
                      {{ order.payment_status === 'paid' ? 'Paid' : 'Pending Payment' }}
                    </span>
                  </div>
                  <button
                    v-if="order.payment_status === 'pending' && order.status === 'pending' && user?.role === USER_ROLES.CUSTOMER"
                    @click="initializePayment"
                    :disabled="isPaying"
                    class="px-4 py-1 mt-2 text-sm font-semibold text-white bg-blue-500 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                    {{ isPaying ? 'Processing...' : 'Make Payment' }}
                  </button>
                  <button v-if="user?.role === USER_ROLES.ADMIN"
                    @click="router.push({ name: ROUTES.ADMIN_BUSINESS_PAYMENT_BREAKDOWN, params: { id: order.uuid } })"
                    class="px-4 py-1 mt-2 text-sm font-semibold text-white bg-blue-500 rounded hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                    View Payment Breakdown
                  </button>

                </div>
                <div class="sm:text-right">
                  <p class="text-sm text-gray-500 dark:text-gray-400">Order Date/Time</p>
                  <p class="font-medium">{{ new Date(order.created_at).toLocaleDateString() }} / {{
                    formatTime(order.created_at) }}</p>
                </div>
              </div>
              <!-- <div class="flex justify-center mt-4">
                <div class="flex items-center">
                  <template v-for="(status, index) in orderStatuses" :key="index">
                    <div class="bg-gray-200 rounded-full size-4" :class="{'bg-green-500': order.status === index}"></div>
                    <div class="sm:w-[4rem] w-[2rem] h-[4px] bg-gray-200" v-if="index !== 'refunded'"/>
                  </template>
</div>
</div> -->
            </div>
          </div>
        </div>

        <!-- Order Details -->
        <div class="overflow-hidden mb-8 bg-white shadow-sm dark:bg-gray-800 sm:rounded-lg">
          <div class="p-6 text-gray-900 dark:text-gray-100">
            <h3 class="mb-4 text-lg font-semibold sm:text-xl">Order Details</h3>
            <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
              <div>
                <h4 class="mb-2 font-medium sm:text-lg">Delivery Information</h4>
                <p class="text-gray-600 dark:text-gray-300">{{ order.delivery_address }}</p>
                <p class="text-gray-600 dark:text-gray-300" v-if="user?.role !== USER_ROLES.BUSINESS">{{ order.phone }}
                </p>
                <p v-if="order.notes" class="mt-2 text-gray-600 dark:text-gray-300">
                  <span class="font-medium">Notes:</span> {{ order.notes }}
                </p>
              </div>
              <div :class="{'cursor-pointer': user.role === 'admin'}" @click="user.role === 'admin' ? router.push({ name: ROUTES.ADMIN_USER, params: { id: order.business.user.id } }) : () => {}">
                <h4 class="mb-2 font-medium sm:text-lg">Business Information</h4>
                <p class="text-gray-600 dark:text-gray-300">{{ order.business?.name }}</p>
                <p class="text-gray-600 dark:text-gray-300">{{ order.business?.address }}</p>
                <p class="text-gray-600 dark:text-gray-300" v-if="user?.role !== USER_ROLES.CUSTOMER">{{
                  order.business?.phone }}</p>
              </div>
              <div :class="{ 'cursor-pointer': user.role === 'admin' }"
                @click="user.role === 'admin' ? router.push({ name: ROUTES.ADMIN_USER, params: { id: order.customer.id } }) : () => { }">
                <h4 class="mb-2 font-medium sm:text-lg">Customer Information</h4>
                <p class="text-gray-600 dark:text-gray-300">{{ order.customer?.name }}</p>
                <p class="text-gray-600 dark:text-gray-300">{{ order.customer?.address }}</p>
                <p class="text-gray-600 dark:text-gray-300" v-if="user?.role !== USER_ROLES.BUSINESS">{{
                  order.customer?.phone }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Order Items -->
        <div class="overflow-hidden bg-white shadow-sm dark:bg-gray-800 sm:rounded-lg">
          <div class="p-6 text-gray-900 dark:text-gray-100">
            <h3 class="mb-4 text-lg font-semibold sm:text-xl">Order Items</h3>
            <div class="overflow-x-auto">
              <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead class="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th
                      class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-300">
                      Item
                    </th>
                    <th
                      class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-300">
                      Quantity
                    </th>
                    <th
                      class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-300">
                      Price
                    </th>
                    <th
                      class="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase dark:text-gray-300">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200 dark:bg-gray-800 dark:divide-gray-700">
                  <tr v-for="item in order.items" :key="item.id">
                    <td class="px-6 py-4">
                      <img :src="item.product.image_url" alt="Item Image"
                        class="object-cover w-10 h-10 rounded-full cursor-pointer" v-if="item.product.image_url"
                        @click="openImage(item.product.image_url)" />
                      <div class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ item.name }}</div>
                      <div class="w-40 text-sm text-gray-500 sm:w-[20rem] dark:text-gray-400">{{ showMoreItemId ===
                        item.id ? item.description : item.description?.slice(0, 30) }} <span
                          class="inline-block ml-2 text-blue-500 cursor-pointer" @click="showMoreDescription(item)">{{
                            showMoreItemId === item.id ? 'show less' : 'show more'}}</span></div>
                    </td>
                    <td class="px-6 py-4 text-sm text-gray-500 whitespace-nowrap dark:text-gray-300">
                      {{ item.quantity }}
                    </td>
                    <td class="px-6 py-4 text-sm text-gray-500 whitespace-nowrap dark:text-gray-300">
                      {{ nairaSymbol }}{{ item.price }}
                    </td>
                    <td class="px-6 py-4 text-sm text-gray-500 whitespace-nowrap dark:text-gray-300">
                      {{ nairaSymbol }}{{ (item.price * item.quantity).toFixed() }}
                    </td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr v-if="order.packs">
                    <td colspan="3" class="px-6 py-4 text-sm font-medium text-right text-gray-500 dark:text-gray-300">
                      Packs ({{ nairaSymbol }}{{ order.price_per_pack }} each) * {{ order.packs }}
                    </td>
                    <td class="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-gray-100">
                      {{ nairaSymbol }}{{ order.packs * order.price_per_pack }}
                    </td>
                  </tr>
                  <tr>
                    <td colspan="3" class="px-6 py-4 text-sm font-medium text-right text-gray-500 dark:text-gray-300">
                      Subtotal
                    </td>
                    <td class="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-gray-100">
                      {{ nairaSymbol }}{{ Number(order.subtotal).toFixed() }}
                    </td>
                  </tr>
                  <tr v-if="user?.role === USER_ROLES.BUSINESS">
                    <td colspan="3" class="px-6 py-4 text-sm font-medium text-right text-gray-500 dark:text-gray-300">
                      Service Fee
                    </td>
                    <td class="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-gray-100">
                      {{ nairaSymbol }}{{ Number(order.business_service_fee).toFixed() }}
                    </td>
                  </tr>
                  <tr v-if="user?.role === USER_ROLES.CUSTOMER">
                    <td colspan="3" class="px-6 py-4 text-sm font-medium text-right text-gray-500 dark:text-gray-300">
                      Service Fee
                    </td>
                    <td class="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-gray-100">
                      {{ nairaSymbol }}{{ Number(order.service_fee).toFixed() }}
                    </td>
                  </tr>
                  <tr v-if="user?.role === USER_ROLES.CUSTOMER">
                    <td colspan="3" class="px-6 py-4 text-sm font-medium text-right text-gray-500 dark:text-gray-300">
                      Delivery Fee
                    </td>
                    <td class="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-gray-100">
                      {{ nairaSymbol }}{{ Number(order.delivery_fee).toFixed() }}
                    </td>
                  </tr>
                  <tr v-if="user?.role === USER_ROLES.CUSTOMER">
                    <td colspan="3" class="px-6 py-4 text-sm font-medium text-right text-gray-500 dark:text-gray-300">
                      Total
                    </td>
                    <td class="px-6 py-4 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-gray-100">
                      {{ nairaSymbol }}{{ total.toFixed() }}
                    </td>
                  </tr>
                  <tr v-if="user?.role === USER_ROLES.BUSINESS">
                    <td colspan="3" class="px-6 py-4 text-sm font-medium text-right text-gray-500 dark:text-gray-300">
                      Net Payout
                    </td>
                    <td class="px-6 py-4 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-gray-100">
                      {{ nairaSymbol }}{{ Number(order.subtotal - order.business_service_fee).toFixed() }}
                    </td>
                  </tr>
                  <template v-if="user?.role === USER_ROLES.ADMIN">
                    <tr>
                      <td colspan="3" class="px-6 py-4 text-sm font-medium text-right text-gray-500 dark:text-gray-300">
                        Customer Service Fee
                      </td>
                      <td class="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-gray-100">
                        {{ nairaSymbol }}{{ Number(order.service_fee).toFixed() }}
                      </td>
                    </tr>
                    <tr>
                      <td colspan="3" class="px-6 py-4 text-sm font-medium text-right text-gray-500 dark:text-gray-300">
                        Customer Delivery Fee
                      </td>
                      <td class="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-gray-100">
                        {{ nairaSymbol }}{{ Number(order.delivery_fee).toFixed() }}
                      </td>
                    </tr>
                    <tr>
                      <td colspan="3" class="px-6 py-4 text-sm font-medium text-right text-gray-500 dark:text-gray-300">
                        Customer Total
                      </td>
                      <td class="px-6 py-4 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-gray-100">
                        {{ nairaSymbol }}{{ total.toFixed() }}
                      </td>
                    </tr>
                    <tr>
                      <td colspan="3" class="px-6 py-4 text-sm font-medium text-right text-gray-500 dark:text-gray-300">
                        Business Service Fee
                      </td>
                      <td class="px-6 py-4 text-sm font-medium text-gray-900 whitespace-nowrap dark:text-gray-100">
                        {{ nairaSymbol }}{{ Number(order.business_service_fee).toFixed() }}
                      </td>
                    </tr>
                    <tr>
                      <td colspan="3" class="px-6 py-4 text-sm font-medium text-right text-gray-500 dark:text-gray-300">
                        Business Net Payout
                      </td>
                      <td class="px-6 py-4 text-sm font-bold text-gray-900 whitespace-nowrap dark:text-gray-100">
                        {{ nairaSymbol }}{{ Number(order.subtotal - order.business_service_fee).toFixed() }}
                      </td>
                    </tr>
                  </template>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
      <!-- No Order Found -->
      <div v-else class="relative px-4 py-3 text-yellow-700 bg-yellow-100 rounded border border-yellow-400"
        role="alert">
        <strong class="font-bold">No order found!</strong>
        <span class="block sm:inline">The requested order could not be found.</span>
      </div>
    </div>
  </div>
  <ImageModal :imageUrl="imageUrl" v-model:visible="isImageModalOpen" header="Product Image" />
  <ConfirmationModal :show="showConfirmDialog" @close="showConfirmDialog = false">
    <template #title>
      Confirm Status Update
    </template>
    <template #content>
      <p>
        Are you sure you want to update the order status to "{{ orderStatusLabels[order.status] }}"? This action cannot
        be undone.
      </p>
    </template>
    <template #footer>
      <SecondaryButton class="mr-2" @click="cancelUpdateOrderStatus">
        Cancel
      </SecondaryButton>
      <DangerButton @click="updateStatusLogic(order.status)" :disabled="isUpdatingStatus">
        {{ isUpdatingStatus ? 'Updating...' : 'Update Status' }}
      </DangerButton>
    </template>
  </ConfirmationModal>
</template>

<script setup lang="ts">
// import { openExternalLink } from '@/utils/externalLinks'
import { formatTime } from '@/utils'
import { USER_ROLES } from '@/types'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useOrderStore } from '@/stores/orderStore'
import { storeToRefs } from 'pinia'
import { nairaSymbol } from '@/constants'
import { useAuthStore } from '@/stores/authStore'
import { useToast } from 'primevue/usetoast'
import { ROUTES } from '@/router/routeNames'
import { orderStatusLabels, statusColors } from '@/constants'
import ImageModal from '@/components/modals/ImageModal.vue'
import SecondaryButton from '@/components/general/SecondaryButton.vue';
import DangerButton from '@/components/general/DangerButton.vue';
import ConfirmationModal from '@/components/general/ConfirmationModal.vue';
import PaystackPop from '@paystack/inline-js';

const toast = useToast()
const router = useRouter()
const authStore = useAuthStore()
const { user } = storeToRefs(authStore)
const route = useRoute();
const orderStore = useOrderStore()
const { order } = storeToRefs(orderStore) // Get reactive reference to the order
const isLoading = ref(true)
const isUpdatingStatus = ref(false)
const error = ref<string | null>(null)
const isOnline = ref(navigator.onLine)
const isPaying = ref(false)
const isImageModalOpen = ref(false)
const imageUrl = ref('')
const showConfirmDialog = ref(false)
const oldStatus = ref('')
const paystackInstance = new PaystackPop();
// Update online status when connectivity changes
const updateOnlineStatus = () => {
  isOnline.value = navigator.onLine
}

const showMoreItemId = ref(null)
const fetchSingleOrder = async () => {
  await orderStore.fetchSingleOrder(route.params.id as string)
}

const initializePayment = () => {
  paystackInstance.newTransaction({
    key: import.meta.env.VITE_PAYSTACK_PUBLIC_KEY,
    email: user.value.email,
    firstName: user.value.name.split(' ')[0],
    lastName: user.value.name.split(' ')[1],
    phone: user.value.phone,
    amount: total.value * 100,
    channels: ['card', 'bank', 'ussd', 'qr', 'eft', 'mobile_money', 'bank_transfer', 'apple_pay'],
    onSuccess: async (transaction) => {
      await payOrder(transaction.reference)
    },
    onError: (error) => {
      console.error('Payment error:', error)
      toast.add({ summary: 'Failed', detail: error.message, severity: 'error', life: 5000 })
    }
  })
}

const payOrder = async (reference: string) => {
  isPaying.value = true
  try {
    const response = await orderStore.payOrder(route.params.id as string, reference);
    if (response.order) {
      await fetchSingleOrder()
      toast.add({ summary: 'Success', detail: 'Order paid successfully', severity: 'success', life: 5000 })
    } else {
      toast.add({ summary: 'Failed', detail: response.error, severity: 'error', life: 5000 })
    }
  } catch (error) {
    console.error('Payment error:', error)
  } finally {
    isPaying.value = false
  }
}
watch(() => order.value?.status, (newStatus, previousStatus) => {
  if (newStatus === 'cancelled' || newStatus === 'refunded' || newStatus === 'delivered') {
    oldStatus.value = previousStatus
  }
})

const cancelUpdateOrderStatus = () => {
  showConfirmDialog.value = false
  order.value.status = oldStatus.value
  oldStatus.value = ''
}

const updateStatusLogic = async (status: string) => {
  isUpdatingStatus.value = true
  try {
    await orderStore.updateOrderStatus(route.params.id as string, status)
    await fetchSingleOrder()
    toast.add({
      summary: 'Success',
      detail: 'Order status updated successfully',
      severity: 'success',
      life: 5000
    })
  } catch (err) {
    if (err.response.data.message) {
      toast.add({
        summary: 'Failed',
        detail: err.response.data.message,
        severity: 'error',
        life: 5000
      })
      await fetchSingleOrder()
      return;
    }
    toast.add({
      summary: 'Failed',
      detail: 'Failed to update order status',
      severity: 'error',
      life: 5000
    })
  } finally {
    showConfirmDialog.value = false
    isUpdatingStatus.value = false
  }
}


const updateOrderStatus = async (event: InputEvent) => {
  const status = (event.target as HTMLSelectElement).value
  if (status === 'cancelled' || status === 'refunded' || status === 'delivered') {
    showConfirmDialog.value = true
    return;
  }
  updateStatusLogic(status)
}

const showMoreDescription = (item: any) => {
  if (showMoreItemId.value === item.id) {
    showMoreItemId.value = null
  } else {
    showMoreItemId.value = item.id
  }
}

// Function to manually check connection and refresh data if online
const checkConnection = async () => {
  updateOnlineStatus()
  if (isOnline.value) {
    isLoading.value = true
    try {
      await fetchSingleOrder()
    } catch (err) {
      error.value = 'Failed to load order details'
    } finally {
      isLoading.value = false
    }
  }
}



onMounted(async () => {
  // Add event listeners for online/offline events
  window.addEventListener('online', updateOnlineStatus)
  window.addEventListener('offline', updateOnlineStatus)
  try {
    isLoading.value = true
    // Only fetch data if online
    if (isOnline.value) {
      await fetchSingleOrder()
    }
    isLoading.value = false
  } catch (err) {
    error.value = 'Failed to load order details'
    isLoading.value = false
  }
})

onUnmounted(() => {
  // Clean up event listeners
  window.removeEventListener('online', updateOnlineStatus)
  window.removeEventListener('offline', updateOnlineStatus)
})


const total = computed(() => {
  if (!order.value) return 0
  return Number(order.value.subtotal) + Number(order.value.service_fee) + Number(order.value.delivery_fee)
})


// const handlePayment = async (paymentUrl: string) => {
//   try {
//     if (paymentUrl) {
//       // Use the external links utility which handles PWA vs browser mode
//       await openExternalLink(paymentUrl, true, {
//         returnUrl: window.location.href,
//         orderId: order.value?.id,
//       })
//     }
//   } catch (error) {
//     console.error('Payment error:', error)
//     toast.add({
//       severity: 'error',
//       summary: 'Payment Error',
//       detail: 'There was a problem processing your payment. Please try again.',
//       life: 5000,
//     })
//     // Fallback in case of any errors
//     window.location.href = paymentUrl
//   }
// }

const openImage = (url: string) => {
  imageUrl.value = url
  isImageModalOpen.value = true
}
</script>
